import { useState, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { isPasskeySupported, loginWithPasskey } from "../lib/webauthn";

type Status = "idle" | "submitting" | "sent" | "error";

export default function LoginPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);
  const [passkeyBusy, setPasskeyBusy] = useState(false);

  // AUFTRAG E: Passkey = primärer Login (1 Tap, MFA in einem Schritt).
  // Magic-Link+TOTP bleibt vollständig als Fallback darunter.
  const onPasskey = async () => {
    setPasskeyBusy(true);
    setError(null);
    try {
      await loginWithPasskey();
      await qc.invalidateQueries({ queryKey: ["auth", "me"] });
      navigate("/", { replace: true });
    } catch (err) {
      // Abbruch des OS-Dialogs ist kein Fehlerzustand, den man rot anmalen
      // muss — aber unbekannte Passkeys/Serverfehler schon.
      const msg = err instanceof Error ? err.message : "Passkey-Login fehlgeschlagen";
      if (!/abort|abgebrochen|timed out|NotAllowed/i.test(msg)) {
        setError(msg);
      }
    } finally {
      setPasskeyBusy(false);
    }
  };

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("submitting");
    setError(null);
    try {
      const res = await fetch("/api/v1/auth/magic-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok && res.status !== 202) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail ?? `HTTP ${res.status}`);
      }
      setStatus("sent");
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "Unbekannter Fehler");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-md rounded-2xl bg-slate-900/60 backdrop-blur card-glow-accent-blue p-8">
        <h1 className="text-2xl font-semibold mb-1 text-slate-100 flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-sky-400 animate-pulse" />
          X-Holding Cockpit
        </h1>
        <p className="text-sm text-slate-400 mb-6">
          Ein Tap mit Passkey — oder Magic-Link an deine Holding-Mail.
        </p>

        {isPasskeySupported() && (
          <div className="mb-6">
            <button
              type="button"
              onClick={onPasskey}
              disabled={passkeyBusy}
              className="w-full rounded-lg bg-slate-100 hover:bg-white text-slate-900 font-medium py-2.5 disabled:opacity-40 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
            >
              <span aria-hidden>🔑</span>
              {passkeyBusy ? "Warte auf Passkey …" : "Mit Passkey anmelden"}
            </button>
            <div className="flex items-center gap-3 mt-5">
              <span className="h-px flex-1 bg-slate-700/60" />
              <span className="text-[10px] uppercase tracking-wider text-slate-500">
                oder per Mail
              </span>
              <span className="h-px flex-1 bg-slate-700/60" />
            </div>
          </div>
        )}

        {status === "sent" ? (
          <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-4 text-emerald-300 text-sm">
            Falls die Adresse berechtigt ist, liegt der Link in deiner Inbox
            (15 Min gültig).
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-xs font-medium text-slate-400 mb-1">
                E-Mail
              </label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-slate-700 bg-slate-950/50 text-slate-100 px-3 py-2 placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                placeholder="kevin@drainx.de"
                disabled={status === "submitting"}
              />
            </div>

            {error && (
              <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg p-2">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={status === "submitting" || !email}
              className="w-full rounded-lg bg-sky-500 hover:bg-sky-400 text-white font-medium py-2 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              {status === "submitting" ? "Sende…" : "Magic-Link anfordern"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
